import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="back-link">← Back to Home</Link>
      </nav>
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: September 7, 2026</p>

        <h2>1. Acceptance of Terms</h2>
        <p>Welcome to PriceDiary ("we," "our," or "us"). By downloading, installing, accessing, or using the PriceDiary mobile application (the "App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our App.</p>
        <p>PriceDiary is a price tracking and comparison application that helps users track product prices, compare prices across different stores, scan receipts, and share price information with the community.</p>

        <h2>2. Description of Service</h2>
        <p>PriceDiary provides the following services:</p>
        <ul>
          <li><strong>Price Tracking:</strong> Track and monitor product prices over time</li>
          <li><strong>Price Comparison:</strong> Compare prices across different stores and locations</li>
          <li><strong>Receipt Scanning:</strong> Scan receipts to automatically record purchases</li>
          <li><strong>Store Finder:</strong> Find stores with the best prices for specific products</li>
          <li><strong>Community Features:</strong> Share price information and insights with other users</li>
          <li><strong>Purchase History:</strong> Maintain a history of your purchases and spending</li>
          <li><strong>Insights & Analytics:</strong> View spending patterns and price trends</li>
        </ul>

        <h2>3. User Accounts</h2>
        <h3>3.1 Account Creation</h3>
        <p>To use certain features of the App, you must create an account. You agree to:</p>
        <ul>
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Maintain and update your account information to keep it accurate</li>
          <li>Maintain the security of your account credentials</li>
          <li>Accept responsibility for all activities that occur under your account</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
        </ul>

        <h3>3.2 Account Eligibility</h3>
        <p>You must be at least 13 years old to use PriceDiary. If you are under 18, you represent that you have your parent's or guardian's permission to use the App.</p>

        <h2>4. User Conduct</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the App for any illegal purpose or in violation of any laws</li>
          <li>Post false, misleading, or fraudulent price information</li>
          <li>Harass, abuse, or harm other users</li>
          <li>Impersonate any person or entity</li>
          <li>Interfere with or disrupt the App or servers</li>
          <li>Attempt to gain unauthorized access to the App or related systems</li>
          <li>Use automated systems (bots, scrapers) to access the App without permission</li>
          <li>Copy, modify, or distribute the App or its content without authorization</li>
          <li>Reverse engineer or attempt to extract source code from the App</li>
        </ul>

        <h2>5. User-Generated Content</h2>
        <h3>5.1 Content Ownership</h3>
        <p>You retain ownership of any content you submit to the App, including price information, reviews, comments, and photos. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display your content within the App.</p>

        <h3>5.2 Content Guidelines</h3>
        <p>You are solely responsible for the content you submit. You agree that your content:</p>
        <ul>
          <li>Is accurate and truthful</li>
          <li>Does not violate any third-party rights (copyright, trademark, privacy, etc.)</li>
          <li>Is not defamatory, obscene, or offensive</li>
          <li>Does not contain spam or unsolicited commercial content</li>
        </ul>

        <h3>5.3 Content Removal</h3>
        <p>We reserve the right to remove any content that violates these Terms or is otherwise objectionable, at our sole discretion.</p>

        <h2>6. Privacy and Data Protection</h2>
        <p>Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the App, you consent to the collection and use of your information as described in our <Link to="/privacy-policy">Privacy Policy</Link>.</p>

        <h2>7. Intellectual Property</h2>
        <h3>7.1 Our Rights</h3>
        <p>The App, including its design, features, functionality, and content (excluding user-generated content), is owned by PriceDiary and protected by copyright, trademark, and other intellectual property laws.</p>

        <h3>7.2 Limited License</h3>
        <p>We grant you a limited, non-exclusive, non-transferable license to use the App for personal, non-commercial purposes in accordance with these Terms.</p>

        <h2>8. Third-Party Services</h2>
        <p>The App may integrate with third-party services, including:</p>
        <ul>
          <li>Google Sign-In for authentication</li>
          <li>Firebase for data storage and authentication</li>
          <li>Google Places API for location services</li>
          <li>Other third-party services as needed</li>
        </ul>
        <p>Your use of third-party services is subject to their respective terms of service and privacy policies. We are not responsible for the practices of third-party services.</p>

        <h2>9. Disclaimers</h2>
        <h3>9.1 Service Availability</h3>
        <p>We strive to provide reliable service but do not guarantee that the App will be available at all times or free from errors, interruptions, or security issues.</p>

        <h3>9.2 Price Information</h3>
        <p>While we make efforts to ensure price information is accurate, we cannot guarantee the accuracy, completeness, or timeliness of price data. Prices may change without notice, and actual prices at stores may differ from those shown in the App.</p>

        <h3>9.3 No Warranty</h3>
        <p>THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</p>

        <h2>10. Limitation of Liability</h2>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, PRICEDIARY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE APP.</p>

        <h2>11. Indemnification</h2>
        <p>You agree to indemnify and hold harmless PriceDiary, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the App, violation of these Terms, or infringement of any rights of another party.</p>

        <h2>12. Termination</h2>
        <h3>12.1 Termination by You</h3>
        <p>You may stop using the App at any time by deleting your account through the App settings.</p>

        <h3>12.2 Termination by Us</h3>
        <p>We may suspend or terminate your access to the App at any time, with or without notice, for any reason, including if you violate these Terms.</p>

        <h3>12.3 Effect of Termination</h3>
        <p>Upon termination, your right to use the App will immediately cease. We may delete your account and associated data, subject to our Privacy Policy and applicable law.</p>

        <h2>13. Changes to Terms</h2>
        <p>We reserve the right to modify these Terms at any time. We will notify users of material changes by updating the "Last Updated" date at the top of this page or through in-app notifications. Your continued use of the App after changes become effective constitutes acceptance of the modified Terms.</p>

        <h2>14. Governing Law</h2>
        <p>These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which PriceDiary operates, without regard to its conflict of law provisions.</p>

        <h2>15. Dispute Resolution</h2>
        <p>Any disputes arising from these Terms or your use of the App shall be resolved through good faith negotiation. If a dispute cannot be resolved through negotiation, it shall be subject to the exclusive jurisdiction of the courts in the applicable jurisdiction.</p>

        <h2>16. Severability</h2>
        <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>

        <h2>17. Entire Agreement</h2>
        <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and PriceDiary regarding your use of the App and supersede all prior agreements and understandings.</p>

        <h2>18. Contact Information</h2>
        <div className="contact-info">
          <p><strong>If you have any questions about these Terms of Service, please contact us:</strong></p>
          <p><strong>Email:</strong> <a href="mailto:support@logicalflows.com">support@logicalflows.com</a></p>
          <p><strong>Website:</strong> <a href="https://logicalflows.com/" target="_blank" rel="noopener noreferrer">https://logicalflows.com/</a></p>
        </div>

        <p style={{ marginTop: '30px', textAlign: 'center', color: '#7f8c8d' }}>
          © 2026 PriceDiary / Logical Flows. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default TermsOfService;

